import { BlockStack, Box, Button, Icon, InlineStack, Tooltip, Text, Link, Divider, DescriptionList, Tag } from '@shopify/polaris';
import QuickSearchSeating from 'components/quickSearchSeating';
import QuickSearchVehicle from 'components/quickSearchVehicle';
import SeatIndicator from 'components/SeatIndicator';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import { TypedSeating } from 'queries/seating.query';
import {
  TypedTripManifest,
  useMutateAssign_driver_to_trip,
  useMutateRemove_driver_from_trip,
  useMutateUpdateTrip_manifest,
  useMutateUpdateTrip_manifest_seating_template,
  useMutateRefreshTrip_manifest,
  useMutateToggleLockTripManifest,
} from 'queries/trip_manifest.query';
import { TypedVehicle } from 'queries/vehicle.query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PlusIcon, DeleteIcon, HashtagIcon, RefreshIcon, LockFilledIcon, LockIcon } from '@shopify/polaris-icons';
import TicketInManifest from './ticketManifest';
import QuickSearchUser from 'components/quickSearchUser';
import { ResponsivePopover } from 'components/ResponsivePopover';
import __ from 'languages/index';

// --- Component con: Danh sách lái xe ---

function DriverList({
  drivers,
  onRemove,
  isRemoving,
  onAddDriver,
  onAddAssistant,
  showAddDriver,
  setShowAddDriver,
  showAddAssistant,
  setShowAddAssistant,
}: {
  drivers: any[];
  onRemove: (id: string) => void;
  isRemoving: boolean;
  onAddDriver: (userId: string) => void;
  onAddAssistant: (userId: string) => void;
  showAddDriver: boolean;
  setShowAddDriver: (v: any) => void;
  showAddAssistant: boolean;
  setShowAddAssistant: (v: boolean) => void;
}) {
  const sortedDrivers = useMemo(() => {
    if (!drivers) return [];
    return [...drivers].sort((driverA, driverB) => {
      const roleScore = (driver: any) => (driver?.driver_role === 'driver' ? 0 : 1);
      return roleScore(driverA) - roleScore(driverB);
    });
  }, [drivers]);

  const renderAddPopover = (label: string, show: boolean, setShow: (v: boolean) => void, onAdd: (userId: string) => void) => (
    <ResponsivePopover
      title="Thêm tài xế" // Title cho Modal trên di động
      preferredPosition="below"
      preventCloseOnChildOverlayClick
      autofocusTarget="first-node"
      activator={
        <Tag onClick={() => setShow(true)}>
          <InlineStack align="start" blockAlign="center" gap="200">
            <span>{label}</span>
            <Icon source={PlusIcon} />
          </InlineStack>
        </Tag>
      }
      onClose={() => setShow(false)}
    >
      <QuickSearchUser
        title=""
        placeholder="Gõ để tìm tài xế"
        user_role="driver"
        onClose={(userData: any) => {
          if (userData?.user_id) onAdd(userData.user_id);
          setShow(false);
        }}
      />
    </ResponsivePopover>
  );

  return (
    <BlockStack gap="200">
      {sortedDrivers.map((driver, idx) => (
        <Box key={driver.id + '____' + idx} padding="200" background="bg-surface-secondary" borderRadius="100">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap={'400'} align="start" blockAlign="center">
              <Text as="span" tone="subdued">
                {driver.driver_role === 'driver' ? 'Tài xế' : 'Phụ xe'}
              </Text>
              <Text as="span" fontWeight="semibold">
                <Link url={`tel:${driver.driver_data?.user_phonenumber || '0'}`}>
                  {driver.driver_data?.display_name} ({driver.driver_data?.user_phonenumber || 'chưa có số điện thoại'})
                </Link>
              </Text>
            </InlineStack>
            <Button icon={DeleteIcon} variant="plain" onClick={() => onRemove(String(driver.id))} loading={isRemoving} />
          </InlineStack>
        </Box>
      ))}
      <InlineStack gap="400">
        {renderAddPopover('Thêm tài', showAddDriver, setShowAddDriver, onAddDriver)}
        {renderAddPopover('Thêm phụ', showAddAssistant, setShowAddAssistant, onAddAssistant)}
      </InlineStack>
    </BlockStack>
  );
}

// Component con để hiển thị từng chuyến, chứa logic xóa
export default function ManifestItem({
  manifest,
  onNewTicket,
  editButtonCallback,
  editTaxButtonCallback,
  payButtonCallback,
}: {
  manifest: TypedTripManifest;
  onNewTicket: (trip_manifest_id: any, type: string, position?: string[]) => void;
  editButtonCallback: (order_id: any) => void;
  editTaxButtonCallback: (order_id: any) => void;
  payButtonCallback: (order_id: any) => void;
}) {
  const { addNotification } = useNotification();
  const { showDeleteConfirm } = useDeleteConfirm();

  const { mutateAsync: updateTripManifest } = useMutateUpdateTrip_manifest();
  const { mutateAsync: updateTripManifestSeating } = useMutateUpdateTrip_manifest_seating_template();
  const { mutateAsync: refreshManifest, isPending: refreshingDataForManifest } = useMutateRefreshTrip_manifest();

  const refreshManifestCallback = useCallback(async (manifest_id: any) => {
    try {
      await refreshManifest(manifest_id);
      addNotification('info', 'Đã làm mới dữ liệu chuyến.');
    } catch (e) {}
  }, []);

  // FIX: Thêm hook xóa lái xe và vé, sử dụng mutateAsync
  const { mutateAsync: removeDriver, isPending: isRemovingDriver } = useMutateRemove_driver_from_trip();
  // const { mutateAsync: removeTicket, isPending: isRemovingTicket } = useMutateRemove_ticket_from_trip();
  const { mutateAsync: assignDriverToTrip } = useMutateAssign_driver_to_trip();
  // FIX: Thêm lại nút "Thêm chuyến"

  const handleRemoveDriver = async (tripManifestDriverId: string) => {
    try {
      await removeDriver(tripManifestDriverId);
      addNotification('info', 'Đã xóa lái xe khỏi chuyến.');
    } catch (error: any) {
      addNotification('error', error?.message || 'Lỗi khi xóa lái xe.');
    }
  };

  /** thêm tài xế ở modal của Hiếu */
  const [showAddNewDriverInMemo, setShowAddNewDriverInMemo] = useState(false);
  /** Thêm tài xế */
  const [showAddNewDriver, setShowAddNewDriver] = useState(null);
  const addNewDriverCallback = useCallback(async (trip_manifest_id: any, user_id: string, role: string) => {
    try {
      await assignDriverToTrip({
        trip_manifest_id: String(trip_manifest_id),
        driver_id: user_id,
        driver_role: role ?? 'driver',
      });
      setShowAddNewDriver(null);
      setShowAddNewDriverInMemo(false);
    } catch (e) {
      //   addNotification('error', 'Cập nhật tài xế bị lỗi');
    }
  }, []);

  /** thêm phụ xe */
  const [showAddNewDriverAssistant, setShowAddNewDriverAssistant] = useState(false);
  /** thêm xe */
  const [showAddNewCar, setShowAddNewCar] = useState<boolean>(false);
  const addOrUpdateCarCallback = useCallback(async (manifest_id: any, car_id: string) => {
    try {
      await updateTripManifest({
        id: manifest_id,
        vehicle_id: car_id,
      });
    } catch (e) {
      addNotification('error', 'Cập nhật xe bị lỗi');
    }
  }, []);

  /** thêm loại hình vận chuyển  */
  const [showAddNewSeatingModal, setShowAddNewSeatingModal] = useState(false);
  const addOrUpdateSeatingCallback = useCallback(
    async (seatingModel: TypedSeating) => {
      //
      try {
        const confirmed = await showDeleteConfirm(
          'Thay đổi loại hình vận chuyển?',
          'Khi thay đổi loại hình vận chuyển, các vé trong chuyến này sẽ tạm thời chuyển sang chế độ chưa vào chuyến.'
        );
        if (confirmed)
          await updateTripManifestSeating({
            id: manifest?.id,
            seating_id: seatingModel?.id,
          });
      } catch (e) {
        addNotification('error', 'Cập nhật loại hình vận chuyển lỗi');
      }
    },
    [manifest]
  );

  /** Thêm phí */
  const filteredTickets = useMemo(() => {
    let allPSG = manifest?.trip_manifest_ticket?.filter((val) => val.seat_position !== 'trunk');
    let allSHPMNT = manifest?.trip_manifest_ticket?.filter((val) => val.seat_position === 'trunk');
    return {
      seatValue: allPSG,
      shippingValue: allSHPMNT,
    };
  }, [manifest?.trip_manifest_ticket]);

  /** Toggle trip lock */
  const { mutateAsync: toggleLockTripManifest, isPending: togglinglocktrip } = useMutateToggleLockTripManifest();
  const toggleTripLockCallback = useCallback(async (trip_manifest_id: any) => {
    try {
      await toggleLockTripManifest(trip_manifest_id);
    } catch (e) {}
  }, []);

  return (
    <>
      <div className="manifest_tickets_wrap">
        <Box padding={'400'} borderColor={'border-focus'} borderWidth="050" background="bg-fill" id={`manifest_${manifest.id}`}>
          <div>
            <BlockStack gap="400">
              <BlockStack gap="200">
                {/* Header */}
                <InlineStack align="space-between" blockAlign="center" gap={'300'}>
                  <InlineStack wrap={false} align="start" blockAlign="center" gap="400">
                    <InlineStack align="start" blockAlign="center">
                      <Icon source={HashtagIcon} tone="subdued" />
                      <Tooltip content={`Manifest ID ${String(manifest.id)} / Khởi hành ${manifest.departure_hour}`}>
                        <Text as="h3" variant="headingMd" tone="subdued">
                          {manifest.id} - {manifest.departure_hour}
                        </Text>
                      </Tooltip>
                    </InlineStack>
                    <SeatIndicator
                      totalSeats={manifest?.seating?.seating_available_count}
                      occupiedSeats={manifest.total_occupied_seats}
                      showHint
                      size={12}
                    />
                  </InlineStack>
                  <InlineStack wrap={false} align="start" blockAlign="center" gap="200">
                    <Button
                      loading={togglinglocktrip}
                      icon={manifest?.is_exported_invoice === 1 ? LockFilledIcon : LockIcon}
                      variant="plain"
                      onClick={() => {
                        toggleTripLockCallback(manifest?.id);
                      }}
                    >
                      {manifest?.is_exported_invoice === 1 ? 'Đã khóa ghi đè' : 'Chưa khóa ghi đè'}
                    </Button>

                    <Button
                      icon={RefreshIcon}
                      variant="plain"
                      loading={refreshingDataForManifest}
                      onClick={() => refreshManifestCallback(manifest.id)}
                    >
                      Tính lại
                    </Button>
                  </InlineStack>
                </InlineStack>

                {/* Vehicle */}
                <Box>
                  <InlineStack blockAlign="center" gap="200">
                    {manifest?.is_exported_invoice === 1 ? (
                      <>
                        <Text as="h3" variant="headingLg">
                          {manifest?.vehicle?.license_plate ?? 'Chưa thêm xe'}
                        </Text>
                        <Text as="p" variant="bodyLg">
                          {manifest?.seating?.seating_name || 'Chưa thêm, click để thêm'}
                        </Text>
                      </>
                    ) : (
                      <>
                        <ResponsivePopover
                          title="Thêm xe vào chuyến" // Title cho Modal trên di động
                          preferredPosition="below"
                          preventCloseOnChildOverlayClick
                          autofocusTarget="first-node"
                          active={showAddNewCar}
                          activator={
                            <Link monochrome removeUnderline onClick={() => setShowAddNewCar(true)}>
                              <Text as="h3" variant="headingLg">
                                {manifest?.vehicle?.license_plate ?? 'Chưa thêm xe'}
                              </Text>
                            </Link>
                          }
                          onClose={() => setShowAddNewCar(false)}
                        >
                          <QuickSearchVehicle
                            onSelect={function (vehicle: TypedVehicle | null): void {
                              addOrUpdateCarCallback(manifest.id, vehicle.id);
                              setShowAddNewCar(false);
                            }}
                            title={''}
                          />
                        </ResponsivePopover>
                        <ResponsivePopover
                          title="Chọn loại hình vận chuyển" // Title cho Modal trên di động
                          preferredPosition="below"
                          preventCloseOnChildOverlayClick
                          autofocusTarget="first-node"
                          active={showAddNewSeatingModal}
                          activator={
                            <Button variant="plain" disclosure onClick={() => setShowAddNewSeatingModal(true)}>
                              Loại hình: {manifest?.seating?.seating_name || 'Chưa thêm, click để thêm'}
                            </Button>
                          }
                          onClose={() => setShowAddNewSeatingModal(false)}
                        >
                          <QuickSearchSeating
                            current_seating_id={manifest?.seating?.id}
                            title={''}
                            onClose={(v) => {
                              addOrUpdateSeatingCallback(v);
                              setShowAddNewSeatingModal(false);
                            }}
                          />
                        </ResponsivePopover>
                      </>
                    )}
                  </InlineStack>
                </Box>

                {/** Price */}
                <Box>
                  <InlineStack gap={'400'} align="start" blockAlign="center">
                    <Text as="span">Tổng</Text>
                    <Text as="span" variant="headingLg">
                      {__helpers.formatNumberWithCommas(manifest.total_fare)}
                    </Text>
                  </InlineStack>
                </Box>
              </BlockStack>
              <Divider />

              <Box>
                <DescriptionList
                  items={[
                    {
                      term: 'Tài xế',
                      description: (
                        <>
                          <DriverList
                            drivers={manifest.trip_manifest_driver}
                            onRemove={handleRemoveDriver}
                            isRemoving={isRemovingDriver}
                            onAddDriver={(userId) => addNewDriverCallback(manifest?.id, userId, 'driver')}
                            onAddAssistant={(userId) => addNewDriverCallback(manifest?.id, userId, 'assistant')}
                            showAddDriver={showAddNewDriverInMemo}
                            setShowAddDriver={setShowAddNewDriverInMemo}
                            showAddAssistant={showAddNewDriverAssistant}
                            setShowAddAssistant={setShowAddNewDriverAssistant}
                          />
                        </>
                      ),
                    },
                  ]}
                />

                {/* --- Component con: Danh sách lái xe --- */}
              </Box>

              {/* Tickets */}
              <BlockStack gap="400">
                <Divider />
                <Text as="h3" variant="headingMd">
                  Vé khách
                </Text>
                {/* --- Component con: Danh sách vé --- */}
                <TicketInManifest
                  ticketData={filteredTickets.seatValue || []}
                  manifest={manifest}
                  type="seat"
                  editButtonCallback={editButtonCallback}
                  editTaxButtonCallback={editTaxButtonCallback}
                  payButtonCallback={payButtonCallback}
                  onNewTicket={onNewTicket}
                />

                <Divider />
                <Text as="h3" variant="headingMd">
                  Vé hàng
                </Text>
                <TicketInManifest
                  ticketData={filteredTickets.shippingValue || []}
                  manifest={manifest}
                  type="shipping"
                  editButtonCallback={editButtonCallback}
                  editTaxButtonCallback={editTaxButtonCallback}
                  payButtonCallback={payButtonCallback}
                  onNewTicket={onNewTicket}
                />
                <br />
              </BlockStack>
            </BlockStack>
          </div>
        </Box>
      </div>

      <br />
      <br />
      <br />
    </>
  );
}

